import { ExcelExportDto, PowerpointExportDto } from '@ecosystem/export-api';
import { actions, overlays } from '@ecosystem/newron-design-system';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { TrackSelectionTypes, trackTableClicked, TrackTableNavTypes } from '../../analytics/utils';
import { Instructions } from '../../content/instructions';
import { DataContentProps } from '../../content/types';
import { useMobile } from '../../context/mobile/mobile.provider';
import { Loading } from '../../design/loading/Loading';
import { SearchInput } from '../../design/search/search';
import { Table } from '../../design/table2';
import { ExportButton, ExportButtonProps } from '../../export/export.button';
import { useCorePageState } from '../../page/provider';
import { PageSettingsManager } from '../../page/settings';
import { ComponentHeaderActionType } from '../../page/types';
import { EventElementType, ExportFormat, ExportOptions, IdentifierColumn, SortingInfo } from '../../types';
import { MobileOverflowMenu } from '../mobileMenu/mobileOverflowMenu';

import { DataRowCells, HeadersBottom, HeadersTop, InfoRowCells } from './board.parts';
import { chartDataToExportData } from './chartDataToExportData';
import { DISABLED_LABLES, MAX_TOTAL_GROUP_ITEM_COUNT } from './constants';
import { CreateGroupModal } from './createGroupModal';
import { CustomiseTable } from './customise';
import { getSortKeysFromColumn, GroupInfo, LeaderboardInfo, makeLeaderboardData, sortLeaderboard } from './data';
import {
  findLeaderboardItemNamesForTracking,
  getGroupDataFromLeaderboard,
  getLeaderboardHeaderString,
  getLeaderboardType,
} from './helper';
import { LeaderboardQuickLook } from './LeaderboardQuickLook';
import { SelectionBar } from './selection.bar';
import { QuickLookOptions, SelectionConfig, TableCustomiseOptions } from './types';
import { useDataFromComponentSettings } from './useDataFromComponentSettings.hook';
import { useProductGroups } from './useProductGroups.hook';

const { Toast } = overlays;

// TODO - hack for removing 'suppressed'
const isDisabled = (rowId: string) => {
  return (
    rowId &&
    Object.values(DISABLED_LABLES).reduce((prev, curr) => {
      return prev || rowId.includes(curr);
    }, false)
  );
};

function Leaderboard({
  data,
  isLoading,
  instructions = {},
  paramsForData,
  dataUrlRoot,
  updateSettings,
  ComponentActionsContainer,
  ComponentFooterContainer,
  componentSettings,
  isEnrichmentLoading,
  trackEvent,
  componentMetadata: componentMetaData,
  collection,
}: DataContentProps) {
  const { updatePageSettings, globalWarnings } = useCorePageState();
  const [rowsToDisplay, setRowsToDisplay] = useState<number>(100);
  const [dataStamp, setDataStamp] = useState<number>();
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardInfo>();
  const [groupData, setGroupData] = useState<GroupInfo[]>();
  const [showGroupData, setShowGroupData] = useState<{ name: string; show: boolean }[]>();
  const [searchTerm, setSearchTerm] = useState<string | undefined>();

  const [sortingInfo, setSortingInfo] = useState<SortingInfo>(undefined);
  const [defaultSortingInfo, setDefaultSortingInfo] = useState<SortingInfo>();

  const [selected, setSelected] = useState<string[]>([]);
  const [disabled, setDisabled] = useState<string[]>([]);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [toast, setToast] = useState<{ type: overlays.Notification; message: string }>();

  const [highlighted, setHighlighted] = useState<string>();
  const visibleColumnsRef = useRef<string>(null);

  const myInstructions = new Instructions(instructions, componentSettings);
  const rowIdField = myInstructions.getString('rowIdField');
  const preferencesGroupName = myInstructions.getString('preferencesGroupName');
  const groupsParam = myInstructions.getString('groupsParam');
  const quickLookOptions = myInstructions.getObject('quickLook') as QuickLookOptions;
  const selectionConfig = myInstructions.getObject('selectionConfig') as SelectionConfig;
  const customiseOptions = myInstructions.getObject('customiseOptions') as TableCustomiseOptions;
  const exportOptions = myInstructions.getObject('export') as ExportOptions;

  const rowNameField = componentMetaData?.tracking?.rowNameField;

  const isLeaderboardLoading = isLoading || isEnrichmentLoading;

  const pageSettingsInstance = PageSettingsManager.getInstance();

  const {
    isLoading: isProductGroupLoading,
    groups,
    isGroupModalOpen,
    createOrUpdateGroup,
    deleteGroup,
    deleteGroupItem,
    toggleCreateGroupModal,
    updateGroupName,
    createGroups,
  } = useProductGroups({
    componentSettings,
    updateSettings,
    preferencesGroupName,
    groupsParam,
    paramsForData,
    trackEvent,
    leaderboardData,
  });

  let visibleColumns: string[] = [];
  if (customiseOptions?.columnsParam) {
    visibleColumns =
      componentSettings[customiseOptions.columnsParam].sort((a, b) => {
        if (a === 'brand_name') return -1;
        if (b === 'brand_name') return 1;
        return 0;
      }) || [];
  }

  const { storeSelectedProducts } = useDataFromComponentSettings({
    componentSettings,
    isLoading,
    rowsToDisplay,
    selected,
    setRowsToDisplay,
    setSelected,
    updateSettings,
  });

  const { isMobile } = useMobile();

  const updateData = (withSearchTerm?: string) => {
    if (!data || !data.hasDataResultSetsValues()) {
      return;
    }
    const pageSettings = pageSettingsInstance.getPageSettings();
    const updatedRowIdField = pageSettings?.rowIdField?.[0] || rowIdField;
    const leaderboardData = makeLeaderboardData(
      data,
      { rowIdField: updatedRowIdField, searchTerm: withSearchTerm, visibleColumns },
      sortingInfo
    );

    setDefaultSortingInfo(leaderboardData.defaultSortingInfo);

    const groupData = getGroupDataFromLeaderboard(leaderboardData, groups, updatedRowIdField);
    setLeaderboardData(leaderboardData);
    setGroupData(groupData);
    setDataStamp(data.stamp);
    setHighlighted(undefined);
    setSelected([]);
    disableIndividualItem(leaderboardData);
  };

  const disableIndividualItem = leaderboardData => {
    //get list of disabled then exclude group from the item list
    const disabledItems = leaderboardData.rowIds.filter(
      rowId => isDisabled(rowId) && !groups.some(group => group.id === rowId)
    );
    setDisabled(disabledItems);
  };

  useEffect(() => {
    const pageSettings = pageSettingsInstance.getPageSettings();
    const sortingInfo = pageSettings?.sortingInfo?.[0] ? JSON.parse(pageSettings?.sortingInfo?.[0]) : undefined;
    setSortingInfo(sortingInfo || defaultSortingInfo);
  }, [defaultSortingInfo]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!data || data.stamp === dataStamp) {
      return;
    }

    const firstDataColumn = data.getDataColumns()[0];
    const newRowIdField = firstDataColumn?.['idField'] ?? rowIdField;
    updateSettings({ rowIdField: [newRowIdField] });
    updatePageSettings({ rowIdField: [newRowIdField] });
    updateData();
    setSearchTerm(undefined);
  }, [data]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (visibleColumnsRef.current === JSON.stringify(visibleColumns)) {
      return;
    }
    visibleColumnsRef.current = JSON.stringify(visibleColumns);
    updateData();
  }, [visibleColumns]);

  useEffect(() => {
    if (!sortingInfo && !sortingInfo?.sortKey) {
      return;
    }
    const infoDataCopy = sortLeaderboard(leaderboardData, sortingInfo);
    setLeaderboardData(infoDataCopy);
    updatePageSettings({
      sortingInfo: [JSON.stringify(sortingInfo)],
    });
  }, [sortingInfo]);

  useEffect(() => {
    if (data) {
      collection?.onLoaded();
    }
  }, [data]);

  const handleLoadMore = () => {
    if (!leaderboardData) {
      return;
    }
    const newAmount = rowsToDisplay + 100;
    if (newAmount > leaderboardData.rowsData.length) {
      setRowsToDisplay(leaderboardData.rowsData.length);
    } else {
      setRowsToDisplay(newAmount);
    }
    trackTableClicked(trackEvent, TrackTableNavTypes.LoadMore, EventElementType.Link);
  };

  const handleSelect = (rowId: string) => {
    if (disabled.includes(rowId)) {
      return;
    }
    if (selected.includes(rowId)) {
      setSelected(selected.filter(v => v !== rowId));
    } else {
      setSelected([...selected, rowId]);
    }
  };

  const handleSelectAll = () => {
    if (!leaderboardData) {
      return;
    }
    const selectedRows = leaderboardData.rowIds.filter(rowId => !disabled.includes(rowId));
    setSelected(selectedRows);
    const numberOfProducts = selectedRows.length;
    trackTableClicked(trackEvent, TrackSelectionTypes.selectAll, EventElementType.CheckBox, {
      numberOfProducts: numberOfProducts.toString(),
    });
  };

  const handleClearSelect = () => {
    setSelected([]);
  };

  const handleHighlight = (rowId: string) => {
    const isCurrentlyHighlighted = rowId === highlighted;
    setHighlighted(isCurrentlyHighlighted ? undefined : rowId);

    if (isCurrentlyHighlighted) {
      return;
    }

    const highlightedItems = findLeaderboardItemNamesForTracking([rowId], rowIdField, rowNameField, leaderboardData);
    trackTableClicked(trackEvent, TrackTableNavTypes.Quickview, EventElementType.Overlay, {
      optionChosen: highlightedItems,
    });
  };

  const handleSearchChange = (term?: string) => {
    setSearchTerm(term);
    updateData(term);
  };

  const handleSortLeaderboard = (sorting: SortingInfo) => {
    setSortingInfo(sorting);
  };

  const { Button } = actions;

  const highlightedRow =
    highlighted !== undefined
      ? leaderboardData.rowsData.find(({ values }) => `${values?.[`${rowIdField}`]}` === highlighted)
      : undefined;

  const handleGroupList = (rowId: string) => {
    setShowGroupData(prev => {
      return prev?.find(group => group.name === rowId)
        ? prev?.map(group => {
            if (group.name === rowId) {
              return { name: rowId, show: !group.show };
            }
            return group;
          })
        : [...(prev || []), { name: rowId, show: true }];
    });
  };

  const getGroup = (rowId: string) => {
    return groups?.find(group => group.id === rowId);
  };

  const findRelatedGroup = (rowId: string) => {
    return groupData?.find(item => item.name === rowId);
  };

  const isGroupVisible = (rowId: string) => {
    return showGroupData?.find(group => group.name === rowId)?.show;
  };

  const handleCreateOrUpdateGroup = (names: string[]) => {
    createOrUpdateGroup(names, selected, rowIdField, rowNameField);
    handleClearSelect();
  };

  const getGroupItemBorderValue = rowId => {
    return isGroupVisible(rowId) ? 'border-light border-t-8 border-l-2' : '';
  };

  const getGroupItemBackground = rowId => {
    return isGroupVisible(rowId) ? 'bg-dark' : '';
  };

  const getGroupModelsBorderValue = (rowId: string, modelId: number) => {
    const groupName = getGroup(rowId)?.name;
    const isLastItem = findRelatedGroup(groupName)?.data.slice(-1)[0].values[`${rowIdField}`] === modelId;
    return isLastItem ? 'border-light border-b-8 border-l-2' : 'border-light border-l-2';
  };

  const onRowHighlight = (rowId: string) => {
    if (getGroup(rowId)) {
      return handleGroupList(rowId);
    } else if (quickLookOptions) {
      return handleHighlight(rowId);
    }
    return undefined;
  };

  const getQuicklookIds = () => {
    return leaderboardData?.rowIds.slice(0, rowsToDisplay).filter(id => !groups?.find(group => group.id === id));
  };

  const isFirstColumnSorted =
    leaderboardData && getSortKeysFromColumn(leaderboardData.columns[0]).includes(sortingInfo?.sortKey);

  const handleAddGroupClick = (selectedCount: number) => {
    const totalItemCountsInGroups = groups.reduce((acc, group) => acc + group.modelIds.length, 0);
    if (totalItemCountsInGroups + selectedCount >= MAX_TOTAL_GROUP_ITEM_COUNT) {
      setToast({
        type: 'error',
        message:
          'The total number of items exceeds the allowed limit for all groups. Please reduce the number of items or contact with your Gfk representative.',
      });
      return;
    }
    toggleCreateGroupModal();
  };

  const exportButtonProps: ExportButtonProps = {
    trackingSourceData: data,
    componentSettings: componentSettings,
    componentMetadata: componentMetaData,
    disabled: isLeaderboardLoading,
    exportFormats: [ExportFormat.Xlsx],
    getExportData: async (exportFormat: ExportFormat): Promise<PowerpointExportDto | ExcelExportDto> => {
      if (exportFormat === ExportFormat.Xlsx) {
        return chartDataToExportData(
          exportFormat,
          exportOptions,
          leaderboardData,
          groupData,
          data.getCurrency(),
          rowIdField,
          globalWarnings
        );
      }
      return undefined;
    },
    trackEvent: trackEvent,
  };

  const renderActionsContainer = () => {
    return (
      <>
        {exportOptions && <ExportButton {...exportButtonProps} actionType={ComponentHeaderActionType.Button} />}
        {customiseOptions && (
          <Button
            testId="customise-btn"
            disabled={isLeaderboardLoading}
            onClick={() => setShowEdit(true)}
            mode="secondary"
            label="settings"
          >
            <Button.Icon>
              <Button.Icon.icons.Option />
            </Button.Icon>
          </Button>
        )}
        <span className={classNames('ml-4', { 'mr-2': isMobile })}>
          <SearchInput
            disabled={isLeaderboardLoading}
            term={searchTerm}
            placeholder="Quick search"
            onChange={handleSearchChange}
          />
        </span>
      </>
    );
  };

  const renderMobileActionsContainer = () => {
    return (
      <>
        <span className="ml-4">
          <SearchInput
            disabled={isLeaderboardLoading}
            term={searchTerm}
            placeholder="Quick search"
            onChange={handleSearchChange}
          />
        </span>
        <MobileOverflowMenu
          isLoading={isLeaderboardLoading}
          isAiEnabled={false}
          canExportDataSet={!!exportOptions}
          canEditKpis={!!customiseOptions}
          handleEditKpi={() => setShowEdit(true)}
          canToggleDataViz={false}
          exportButtonProps={exportButtonProps}
        />
      </>
    );
  };

  return (
    <>
      {
        <div>
          <div className="relative pt-4 w-full max-w-full">
            <Loading isActive={isLoading} withIndicator />
            <Table bottomScrollPosition={!isMobile ? 72 : 0}>
              <Table.Header withRows={false} isStickyTop={isMobile}>
                <Table.Row>
                  {leaderboardData && (
                    <HeadersTop
                      onSelectAll={selectionConfig ? handleSelectAll : undefined}
                      onClearSelect={selectionConfig ? handleClearSelect : undefined}
                      rowsCount={leaderboardData?.rowIds.length || 0}
                      selectedCount={selected.length || 0}
                      columns={leaderboardData.columns}
                      sortingFn={handleSortLeaderboard}
                      sortingInfo={sortingInfo}
                      trackEvent={trackEvent}
                    />
                  )}
                </Table.Row>
                <Table.Row>
                  {leaderboardData && (
                    <HeadersBottom
                      columns={leaderboardData.columns}
                      sortingFn={handleSortLeaderboard}
                      sortingInfo={sortingInfo}
                      isLoading={isEnrichmentLoading}
                      trackEvent={trackEvent}
                    />
                  )}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {leaderboardData && leaderboardData.totalsData && (
                  <Table.Row important>
                    {isMobile && (
                      <Table.Cell colSpan={1} align="left" important isStickyLeft={isMobile}>
                        <></>
                      </Table.Cell>
                    )}
                    <Table.Cell
                      colSpan={selectionConfig && !isMobile ? 2 : 1}
                      align="left"
                      important
                      isSorted={isFirstColumnSorted}
                      isStickyLeft={isMobile}
                      onClick={isMobile ? () => {} : undefined}
                    >
                      Market
                    </Table.Cell>
                    <DataRowCells
                      rowId="market"
                      columns={leaderboardData.columns}
                      row={leaderboardData.totalsData[0]}
                      sortInfo={sortingInfo}
                    />
                  </Table.Row>
                )}
                {leaderboardData &&
                  leaderboardData.rowIds.slice(0, rowsToDisplay).map((rowId, idx) => (
                    <React.Fragment key={idx}>
                      <Table.Row
                        key={`${rowId}_${idx}`}
                        border={getGroupItemBorderValue(rowId)}
                        bgColor={getGroupItemBackground(rowId)}
                      >
                        <InfoRowCells
                          isHighlighted={rowId === highlighted}
                          onHighlight={onRowHighlight}
                          onSelect={selectionConfig ? handleSelect : undefined}
                          column={leaderboardData.columns[0].column as IdentifierColumn}
                          row={leaderboardData.rowsData[idx]}
                          isSelected={selected.includes(rowId)}
                          isDisabled={!Array.isArray(groupData) || disabled.includes(rowId)}
                          isLoading={isLeaderboardLoading}
                          isSorted={isFirstColumnSorted}
                          group={findRelatedGroup(rowId)}
                          allGroups={groupData}
                          onDeleteGroup={deleteGroup}
                          onEditGroup={updateGroupName}
                          isGroupVisible={isGroupVisible(rowId)}
                          rowIdField={rowIdField}
                          leaderboardType={getLeaderboardType(rowIdField)}
                        />
                        <DataRowCells
                          rowId={rowId}
                          isHighlighted={rowId === highlighted}
                          onHighlight={quickLookOptions && !getGroup(rowId) ? handleHighlight : undefined}
                          columns={leaderboardData.columns}
                          row={leaderboardData.rowsData[idx]}
                          sortInfo={sortingInfo}
                        />
                      </Table.Row>
                      {isGroupVisible(rowId) &&
                        findRelatedGroup(rowId)?.data.map((row, idx) => (
                          <Table.Row
                            key={`${row.values[`${rowIdField}`]}_${idx}`}
                            border={getGroupModelsBorderValue(rowId, row.values[`${rowIdField}`] as number)}
                            bgColor={getGroupItemBackground(rowId)}
                          >
                            <InfoRowCells
                              isHighlighted={row.values[`${rowIdField}`] === highlighted}
                              onHighlight={onRowHighlight}
                              onSelect={selectionConfig && !isMobile ? handleSelect : undefined}
                              isSorted={isFirstColumnSorted}
                              column={leaderboardData.columns[0].column as IdentifierColumn}
                              row={row}
                              isLoading={isLeaderboardLoading}
                              isSelected={selected.includes(`${row.values[`${rowIdField}`]}`)}
                              isDisabled={disabled.includes(`${row.values[`${rowIdField}`]}`)}
                              onDeleteGroupItem={deleteGroupItem}
                              rowIdField={rowIdField}
                              leaderboardType={getLeaderboardType(rowIdField)}
                            />
                            <DataRowCells
                              rowId={`${row.values[`${rowIdField}`]}`}
                              columns={leaderboardData.columns}
                              row={row}
                              sortInfo={sortingInfo}
                            />
                          </Table.Row>
                        ))}
                    </React.Fragment>
                  ))}

                {leaderboardData && leaderboardData.rowIds.length === 0 && searchTerm && (
                  <Table.Row>
                    <Table.Cell colSpan={99} align="center">
                      No matches for your search
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {leaderboardData && (
              <div className="py-4 flex items-center gap-4">
                <span>
                  {rowsToDisplay > leaderboardData.rowIds.length &&
                    `${leaderboardData.rowIds.length} out of ${leaderboardData.rowIds.length} items`}
                  {rowsToDisplay <= leaderboardData.rowIds.length &&
                    `${rowsToDisplay} out of ${leaderboardData.rowIds.length} items`}
                </span>
                {rowsToDisplay < leaderboardData.rowIds.length && (
                  <Button mode="simple" onClick={handleLoadMore}>
                    Load more
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      }
      <LeaderboardQuickLook
        leaderboardData={leaderboardData}
        quickLookOptions={quickLookOptions}
        highlightedRow={highlightedRow}
        highlighted={highlighted}
        paramsForData={paramsForData}
        groupsParam={groupsParam}
        dataUrlRoot={dataUrlRoot}
        componentSettings={componentSettings}
        disabled={disabled}
        quicklookIds={getQuicklookIds()}
        setHighlighted={setHighlighted}
        handleHighlight={handleHighlight}
        updateSettings={updateSettings}
      />
      {leaderboardData && selectionConfig && ComponentFooterContainer && (
        <ComponentFooterContainer>
          <SelectionBar
            isLoading={isLeaderboardLoading}
            config={selectionConfig}
            rowsCount={leaderboardData.rowIds.length}
            selectedIds={selected}
            rowIds={leaderboardData.rowIds.filter(
              id => !disabled.includes(id) && !groupData?.map(data => data.name).includes(id)
            )}
            onClearSelect={handleClearSelect}
            onAddGroupClick={handleAddGroupClick}
            onActionClick={storeSelectedProducts}
            trackEvent={trackEvent}
            rowIdField={rowIdField}
            rowNameField={rowNameField}
            leaderboardData={leaderboardData}
            groups={groups}
            groupsParam={groupsParam}
          />
        </ComponentFooterContainer>
      )}
      {leaderboardData && ComponentActionsContainer && (
        <ComponentActionsContainer>
          {!isMobile ? renderActionsContainer() : renderMobileActionsContainer()}
        </ComponentActionsContainer>
      )}
      {leaderboardData && showEdit && (
        <CustomiseTable
          updateSettings={updateSettings}
          data={data}
          onClose={() => setShowEdit(false)}
          customiseOptions={customiseOptions}
          settings={componentSettings}
          dataColumns={leaderboardData.columns}
          trackEvent={trackEvent}
        />
      )}

      <CreateGroupModal
        onCreateGroup={handleCreateOrUpdateGroup}
        onClose={toggleCreateGroupModal}
        isOpen={isGroupModalOpen}
        title={`Add ${selected.length} ${getLeaderboardHeaderString(
          getLeaderboardType(rowIdField),
          selected.length
        )} to`}
        isLoading={isProductGroupLoading}
        selected={selected}
        leaderboardData={leaderboardData}
        createGroups={createGroups}
        leaderboardType={getLeaderboardType(rowIdField)}
      />
      {toast && (
        <Toast type={toast.type} onComplete={() => setToast(undefined)}>
          {toast.message}
        </Toast>
      )}
    </>
  );
}

export { Leaderboard };
